/**
 * Pure builder of the board card's subject line for a change request: who it
 * is about and who sent it, so the office never has to open an item to know.
 */

import type { I18n } from '@lingui/core';
import {
  changeRequestChildIds,
  isContactPrivacySubType,
  type ChangeRequestPayloadView,
  type ChangeRequestSubject,
} from '@/types/changeRequest';

// Generated DTO fields are all optional: drop missing or blank name parts.
function fullName(firstname?: string, lastname?: string): string {
  return [firstname, lastname]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter((p) => p !== '')
    .join(' ');
}

/**
 * The subject text of a change request, or "" when nothing is known.
 *
 * - With children: their full names (a bulk request adds "+N" for the children
 *   the server did not name), then "by <parent>" when a parent sent it.
 * - Without children (`contact_data`): the requesting parent alone.
 * - `employee_data` and `time_correction` (#1979): the employee the request is
 *   about (from the body).
 * - A `contact_data` privacy request (#1876): the requesting parent alone, as
 *   for any `contact_data`; the card shows the type label ("Data access
 *   request", "Account deletion") as the secondary text.
 */
export function buildChangeRequestSubjectText(
  i18n: I18n,
  view: ChangeRequestPayloadView | null | undefined,
  subject: ChangeRequestSubject | null | undefined,
): string {
  if (view?.family === 'employee_data' || view?.family === 'time_correction') {
    const name = view.executor?.body?.employeeName;
    return typeof name === 'string' ? name.trim() : '';
  }
  if (view?.family === 'contact_data' && isContactPrivacySubType(view.sub_type)) {
    return subject?.requester_name?.trim() ?? '';
  }

  const named = (subject?.children ?? [])
    .filter((c): c is NonNullable<typeof c> => !!c)
    .map((c) => fullName(c.firstname, c.lastname))
    .filter((n) => n !== '');
  const total = Math.max(changeRequestChildIds(view ?? null).length, named.length);
  const childPart = named.length > 0
    ? named.join(', ') + (total > named.length ? ` +${total - named.length}` : '')
    : '';
  const requester = subject?.requester_name?.trim() ?? '';

  if (childPart && requester) {
    return `${childPart} · ${i18n._({
      id: 'changeRequest.card.requestedBy',
      message: 'by {name}',
      values: { name: requester },
    })}`;
  }
  return childPart || requester;
}
